import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { formatPrice, calculateDiscount } from '../../utils/formatPrice';
import { getProductImage } from '../../utils/imageHelper';
import { BadgeGroup } from './Badge';
import { Button } from './Button';
import { ShoppingCart, Heart, Star } from 'lucide-react';

function StarRating({ rating, size = 'sm' }) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';

  return (
    <div className="flex items-center gap-0.5">
      {[...Array(fullStars)].map((_, i) => (
        <Star key={`f-${i}`} className={`${iconSize} text-amber-400 fill-amber-400`} />
      ))}
      {hasHalf && (
        <div className="relative">
          <Star className={`${iconSize} text-gray-200 fill-gray-200`} />
          <div className="absolute inset-0 overflow-hidden w-1/2">
            <Star className={`${iconSize} text-amber-400 fill-amber-400`} />
          </div>
        </div>
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <Star key={`e-${i}`} className={`${iconSize} text-gray-200 fill-gray-200`} />
      ))}
    </div>
  );
}

export function ProductCard({ product, variant = 'default' }) {
  const { addToCart } = useCart();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const discount = product.discount || calculateDiscount(product.originalPrice, product.price);
  const showDiscount = discount > 0;
  const hasOriginalPrice = product.originalPrice && product.originalPrice > product.price;

  const currentImage = activeImage || product.image;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 1500);
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
  };

  if (variant === 'horizontal') {
    return (
      <div className="card flex flex-col sm:flex-row gap-0 overflow-hidden">
        <Link
          to={`/product/${product.id}`}
          className="relative w-full sm:w-44 flex-shrink-0 bg-gray-50 block overflow-hidden group/img"
          aria-label={product.name}
        >
          <img
            src={getProductImage(currentImage)}
            alt={product.name}
            className="w-full h-full object-cover min-h-[160px] transition-transform duration-500 group-hover/img:scale-105"
            loading="lazy"
          />
          {product.badges && product.badges.length > 0 && (
            <div className="absolute top-2 left-2 flex flex-col gap-1 pointer-events-none">
              <BadgeGroup badges={product.badges} maxVisible={2} />
            </div>
          )}
        </Link>
        <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between min-w-0">
          <div>
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 text-base">
                <Link
                  to={`/product/${product.id}`}
                  className="hover:text-primary transition-colors duration-200"
                >
                  {product.name}
                </Link>
              </h3>
              {product.size && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-md font-medium">
                  {product.size}
                </span>
              )}
            </div>

            {product.nameUrdu && (
              <p className="font-urdu text-primary text-sm font-semibold mb-1" dir="rtl">{product.nameUrdu}</p>
            )}

            {product.descriptionUrdu && (
              <p className="font-urdu text-gray-700 text-xs mb-2 leading-relaxed bg-herbal-50/70 p-2 rounded-lg border border-herbal-100/70" dir="rtl">
                {product.descriptionUrdu}
              </p>
            )}

            {product.description && (
              <p className="text-gray-500 text-xs mb-2.5 line-clamp-2 leading-relaxed">{product.description}</p>
            )}

            {product.packageItems && product.packageItems.length > 0 && (
              <div className="mb-3 bg-emerald-50/70 p-2.5 rounded-xl border border-emerald-100">
                <p className="text-[11px] font-bold text-emerald-800 mb-1.5 flex items-center gap-1">
                  <span>🎁</span> Includes {product.packageItems.length} Full Products:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {product.packageItems.map((item, idx) => (
                    <span key={idx} className="text-[10px] bg-white text-emerald-900 font-medium px-2 py-0.5 rounded-md border border-emerald-200">
                      ✓ {item.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 mb-3">
              <StarRating rating={product.rating} size="sm" />
              <span className="text-xs text-gray-400">({product.reviews})</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-primary">{formatPrice(product.price)}</span>
              {showDiscount && hasOriginalPrice && (
                <span className="text-gray-400 line-through text-sm">{formatPrice(product.originalPrice)}</span>
              )}
              {showDiscount && (
                <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">-{discount}%</span>
              )}
            </div>
          </div>
          <Button
            onClick={handleAddToCart}
            variant={addedToCart ? 'ghost' : 'primary'}
            size="sm"
            fullWidth
            className="mt-3"
            leftIcon={<ShoppingCart className="w-4 h-4" />}
          >
            {addedToCart ? '✓ Added!' : product.isPackage ? 'Add Complete Package to Cart' : 'Add to Cart'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <article className={`card group flex flex-col h-full ${product.isPackage ? 'border-2 border-emerald-500/40 shadow-md ring-1 ring-emerald-500/20' : ''}`}>
      {/* Image container */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
        <Link
          to={`/product/${product.id}`}
          className="block w-full h-full"
          aria-label={product.name}
        >
          <img
            src={getProductImage(currentImage)}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-107"
            loading="lazy"
          />

          {/* Gradient overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </Link>

        {/* Badges */}
        {product.badges && product.badges.length > 0 && (
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10 pointer-events-none">
            <BadgeGroup badges={product.badges} maxVisible={2} />
          </div>
        )}

        {/* Discount badge top right */}
        {showDiscount && hasOriginalPrice && (
          <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-lg shadow-sm z-10 pointer-events-none">
            -{discount}%
          </div>
        )}

        {/* Wishlist button */}
        <button
          type="button"
          onClick={handleWishlist}
          className={`absolute bottom-3 right-3 p-2 rounded-full shadow-lg transition-all duration-300 z-10 ${
            isWishlisted
              ? 'bg-red-500 text-white opacity-100'
              : 'bg-white/90 backdrop-blur-sm text-gray-400 opacity-0 group-hover:opacity-100'
          }`}
          aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-white' : ''}`} />
        </button>
      </div>

      {/* Package Mini Thumbnails Strip */}
      {product.packageItems && product.packageItems.length > 0 && (
        <div className="bg-emerald-50/60 px-3 py-2 border-b border-emerald-100 flex items-center gap-1.5 overflow-x-auto">
          <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider whitespace-nowrap mr-1">
            {product.packageItems.length} Products:
          </span>
          {product.packageItems.map((item, idx) => (
            <button
              key={idx}
              type="button"
              onMouseEnter={() => setActiveImage(item.image)}
              onMouseLeave={() => setActiveImage(null)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveImage(item.image === activeImage ? null : item.image);
              }}
              title={`${item.name} (${item.size})`}
              className={`w-7 h-7 rounded-md overflow-hidden border transition-all flex-shrink-0 cursor-pointer ${
                activeImage === item.image ? 'border-primary ring-2 ring-primary/40 scale-110' : 'border-gray-200 hover:border-emerald-500'
              }`}
            >
              <img src={getProductImage(item.image)} alt={item.name} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex-1">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-semibold text-gray-900 text-sm line-clamp-1 group-hover:text-primary transition-colors duration-200">
              <Link
                to={`/product/${product.id}`}
                className="hover:text-primary transition-colors duration-200"
              >
                {product.name}
              </Link>
            </h3>
            {product.size && (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md flex-shrink-0 font-medium">
                {product.size}
              </span>
            )}
          </div>

          {product.nameUrdu && (
            <p className="font-urdu text-primary text-xs font-semibold mb-1" dir="rtl">
              {product.nameUrdu}
            </p>
          )}

          {/* Urdu Description */}
          {product.descriptionUrdu && (
            <p className="font-urdu text-gray-700 text-xs mb-2 leading-relaxed line-clamp-2 bg-herbal-50/70 p-2 rounded-lg border border-herbal-100/70" dir="rtl">
              {product.descriptionUrdu}
            </p>
          )}

          {/* English Description */}
          {product.description && (
            <p className="text-gray-400 text-xs mb-2.5 line-clamp-2 leading-relaxed">
              {product.description}
            </p>
          )}

          {/* Package items mini badges */}
          {product.packageItems && product.packageItems.length > 0 && (
            <div className="mb-2.5 space-y-1">
              <div className="flex flex-wrap gap-1">
                {product.packageItems.map((item, idx) => (
                  <span key={idx} className="text-[10px] text-emerald-800 bg-emerald-100/60 font-medium px-1.5 py-0.5 rounded border border-emerald-200/60">
                    ✓ {item.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Star rating */}
          <div className="flex items-center gap-1.5 mb-3">
            <StarRating rating={product.rating} />
            <span className="text-xs font-medium text-gray-600">{product.rating}</span>
            <span className="text-xs text-gray-400">({product.reviews})</span>
          </div>
        </div>

        {/* Price row */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 mb-3">
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-bold text-primary">{formatPrice(product.price)}</span>
            {showDiscount && hasOriginalPrice && (
              <span className="text-gray-400 line-through text-sm">{formatPrice(product.originalPrice)}</span>
            )}
          </div>
        </div>

        <Button
          onClick={handleAddToCart}
          variant={addedToCart ? 'ghost' : 'primary'}
          size="sm"
          fullWidth
          leftIcon={<ShoppingCart className="w-4 h-4" />}
          className={addedToCart ? 'bg-green-50 text-green-700 border border-green-200' : product.isPackage ? 'bg-gradient-to-r from-emerald-700 to-primary hover:from-emerald-800 hover:to-primary-dark font-bold' : ''}
        >
          {addedToCart ? '✓ Added to Cart!' : product.isPackage ? 'Add Complete Package' : 'Add to Cart'}
        </Button>
      </div>
    </article>
  );
}